import {
    Box,
    Button,
    IconButton,
    Portal,
    Switch,
    Tooltip,
    Typography,
    styled
} from "@mui/material";
import {
    DataGridPro,
    GridColDef,
    GridColumnVisibilityModel,
    GridRenderCellParams,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarExport,
    GridToolbarFilterButton
} from "@mui/x-data-grid-pro";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/system";
import { t } from "i18next";
import { enqueueSnackbar } from "notistack";
import Iconify from "src/components/iconify";
import { FlagIcon } from "src/components/iconify/flagIcon";
import countriesJson from "src/utils/countries.json";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import {
    useDisableActiveModeMutation,
    useAddScrapingMutation,
    useDisableScrapingMutation,
    useDisableKoBiddingManualMaxCpcMutation,
    useDisableKoBiddingAutoMaxCpcMutation
} from "src/context/api/activations/api";
import ModalScraping from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-scraping";
import ModalCreateStrategy from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-create-strategy";
import CustomLineChart from "src/components/inline-chart/inline-chart-activation";
import ConfirmDisableModal from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-confirm-disable";
import ModalActivationsManual from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-activations-manual";
import ModalActivationsAuto from "src/sections/overview/dashboards/views/activations/keyword/modals/modal-activations-auto";
import CustomTooltip from "src/components/competitive-intelligence/components/CustomTooltip";
import { formatNumber } from "src/utils/format-number";
import { formatCurrency } from "src/utils/format-currency";

const GreenCheckSwitch = styled(Switch)(({ theme }) => ({
    padding: 8,
    width: 52,
    height: 32,

    "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: 4,
        transitionDuration: "200ms",

        "&.Mui-checked": {
            transform: "translateX(20px)",

            "& .MuiSwitch-thumb": {
                backgroundColor: "#1FA463",
                backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'><polyline points='20 6 9 17 4 12'/></svg>\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
            },

            "& + .MuiSwitch-track": {
                backgroundColor: "#2F3A45",
                opacity: 1,
            }
        }
    },

    "& .MuiSwitch-thumb": {
        width: 24,
        height: 24,
        borderRadius: "50%",
        transition: "all 0.2s ease",

        backgroundColor: "#C83737",
        backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'><line x1='18' y1='6' x2='6' y2='18'/><line x1='6' y1='6' x2='18' y2='18'/></svg>\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
    },

    "& .MuiSwitch-track": {
        borderRadius: 20,
        backgroundColor: "#2F3A45",
        opacity: 1,
        transition: "all 0.2s ease",
    }
}));

const ColumnBadge = ({ field, title, color, bgColor }: { field: string, title: string, color: any, bgColor: any }) => {
    const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

    useEffect(() => {
        const el = document.querySelector(`[data-field="${field}"]`);
        if (el) {
            const rect = el.getBoundingClientRect();
            setPos({ top: rect.top, left: rect.left, width: rect.width });
        }
    }, [field]);

    if (!pos) return null;

    const badgeWidth = 130;
    const centerLeft = pos.left + pos.width / 2 - badgeWidth / 2;

    return (
        <Portal container={document.body}>
            <Box
                sx={{
                    position: "absolute",
                    top: pos.top - 15,
                    left: centerLeft,
                    width: badgeWidth,
                    borderRadius: '11px',
                    background: bgColor,
                    textAlign: "center",
                    fontSize: 13,
                    fontWeight: 600,
                    color: color,
                    textTransform: "uppercase",
                    py: 0.3,
                    zIndex: 0,
                }}
            >
                {title}
            </Box>
        </Portal>
    );
}

export default function DataGridKeywordExact({
    data,
    rowCount,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    localPatches,
    setLocalPatches,
}: {
    data: any[];
    rowCount: number;
    page: number;
    pageSize: number;
    onPageChange: (p: number) => void;
    onPageSizeChange: (s: number) => void;
    localPatches: Record<string | number, any>;
    setLocalPatches: Dispatch<SetStateAction<Record<string | number, any>>>;
}) {
    const theme = useTheme();
    const workspace: any = useCurrentWorkspace();
    const lang = localStorage.getItem('i18nextLng') || 'en';

    const [selection, setSelection] = useState([]);
    const [selectionRows, setSelectionRows] = useState([]);

    const [loading, setLoading] = useState(false);
    const [scrapingDetails, setScrapingDetails] = useState<any[]>([]);

    const handleCloseScraping = useCallback(() => setModalScraping({ open: false, row: null }), []);
    const handleCloseActivationManual = useCallback(() => setModalActivationManual({ open: false, isActive: false, row: null }), []);
    const handleCloseActivationAuto = useCallback(() => setModalActivationAuto({ open: false, isActive: false, row: null }), []);

    const [addScraping] = useAddScrapingMutation();
    const [disableScraping] = useDisableScrapingMutation();
    const [disableActiveMode] = useDisableActiveModeMutation();
    const [disableKoBiddingManualMaxCpc] = useDisableKoBiddingManualMaxCpcMutation();
    const [disableKoBiddingAutoMaxCpc] = useDisableKoBiddingAutoMaxCpcMutation();

    const mergedRows = useMemo(() => {
        return data.map(row =>
            localPatches?.[row.id]
                ? { ...row, ...localPatches[row.id] }
                : row
        );
    }, [data, localPatches]);

    const [modalScraping, setModalScraping] = useState<{ open: boolean; row: any | null }>({
        open: false,
        row: null,
    });

    const [modalActivationManual, setModalActivationManual] = useState<{ open: boolean; isActive: boolean; row: any | null }>({
        open: false,
        isActive: false,
        row: null,
    });

    const [modalActivationAuto, setModalActivationAuto] = useState<{ open: boolean; isActive: boolean; row: any | null }>({
        open: false,
        isActive: false,
        row: null,
    });

    const [modalCreateStrategy, setModalCreateStrategy]: any = useState<{ open: boolean; isActive: boolean; row: any | null }>({
        open: false,
        isActive: false,
        row: null,
    });

    const [confirmDisable, setConfirmDisable] = useState<{ open: boolean; row: any | null, mode: string | null }>({
        open: false,
        row: null,
        mode: null
    });

    const handleToggle = useCallback((row: any, currentStatus: boolean) => {
        if (currentStatus) {
            setConfirmDisable({ open: true, row, mode: "Scraping" });
        } else {
            setModalScraping({
                open: true,
                row: [row],
            });
        }
    }, []);

    const handleDisableScraping = useCallback(
        async (detail: any) => {
            if (!workspace) return;

            try {
                await disableScraping({ id: detail.id }).unwrap();
                enqueueSnackbar(`Scraping disabled successfully`, { variant: "success" });

                const rowToPatch = confirmDisable.row;

                if (!rowToPatch) return;

                setLocalPatches(prev => ({
                    ...prev,
                    [rowToPatch.id]: {
                        ...rowToPatch,
                        scraping_details: []
                    }
                }));

            } catch (err: any) {
                console.error(err);
                enqueueSnackbar(`Failed to disable Scraping`, { variant: "error" });
            }
        },
        [disableScraping, workspace, confirmDisable.row, setLocalPatches]
    );


    const columns = useMemo<GridColDef[]>(
        () => [
            {
                field: "keyword",
                headerName: t("dashboard.global.keyword"),
                renderHeader: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        <span style={{ fontWeight: 'bold' }}>
                            {t("dashboard.global.keyword")}
                        </span>

                        <Iconify
                            icon="proicons:cursor-click"
                            width={20}
                            height={20}
                            className="keyword-header-icon"
                        />
                    </Box>
                ),
                headerClassName: "keyword-header",

                minWidth: 280,
                renderCell: (params) => (
                    <Box
                        sx={{
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1
                        }}
                    >
                        <span>{params.row.keyword}</span>
                        <Typography fontSize={12.5} color={"#919EAB"}>{formatNumber(params.row.clicks_last30d)}</Typography>
                    </Box>
                )
            },
            { field: "keyword_id", headerName: "Keyword ID", minWidth: 200 },
            {
                field: "region_name",
                headerName: "Region",
                minWidth: 200,
                renderCell: ({ value, row }) => {
                    const flag = countriesJson.find((c) => c.name === row?.region_name)?.code?.toLowerCase();

                    return (
                        <Box display="flex" flexDirection="row">
                            <Box display="flex" alignItems="center" sx={{ mr: 1 }}>

                                {flag ? (
                                    <FlagIcon isCircle={false} code={countriesJson.find((c) => c.name === row?.country_name[0])?.code?.toLowerCase()} sx={{ width: 16, height: 16 }} />
                                ) : (
                                    <Iconify icon="flat-color-icons:globe" sx={{ width: 20, height: 20 }} />
                                )}
                            </Box>
                            {row?.region_name}
                        </Box>
                    );
                },
            },
            {
                field: "campaign_names",
                headerName: "Campaign",
                flex: 1,
                renderCell: ({ value, row }) => {
                    const flag = countriesJson.find((c) => c.name === row?.country_name[0])?.code?.toLowerCase();

                    return (
                        <Box display="flex" flexDirection="row" gap={0.5}>
                            {(value || []).map((c: string, i: number) => (
                                <Box key={i} display="flex" alignItems="center" gap={0.5}>
                                    {flag ? (
                                        <FlagIcon isCircle={false} code={countriesJson.find((c) => c.name === row?.country_name[0])?.code?.toLowerCase()} sx={{ width: 16, height: 16 }} />
                                    ) : (
                                        <Iconify icon="flat-color-icons:globe" sx={{ width: 20, height: 20 }} />
                                    )}
                                    <span>{c}</span>
                                </Box>
                            ))}
                        </Box>
                    );
                },
            },
            {
                field: "campaign_ids",
                headerName: "Campaign ID",
                flex: 1,
            },
            {
                field: "ad_group_names",
                headerName: "Ad Group Name",
                flex: 1,
            },
            {
                field: "ad_group_ids",
                headerName: "Ad Group ID",
                flex: 1,
            },
            {
                field: "scraping_status",
                headerName: "Scraping",
                minWidth: 60,
                renderCell: ({ row }) => {
                    const activeDetail = row.scraping_details?.find((s: any) => s.status === true);
                    const freq = activeDetail?.frequency;

                    const switchComponent = (
                        <Box
                            sx={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <GreenCheckSwitch
                                checked={!!activeDetail}
                                onChange={() => handleToggle(row, !!activeDetail)}
                            />
                        </Box>
                    );

                    if (activeDetail) {
                        return (
                            <Tooltip
                                title={freq ? `Frequency: ${freq}` : "No frequency"}
                                placement="top"
                                arrow
                            >
                                {switchComponent}
                            </Tooltip>
                        );
                    }

                    return switchComponent;
                },
            },
            {
                field: "mode",
                headerName: "Mode",
                minWidth: 210,
                sortable: false,
                editable: false,

                renderCell: (params: any) => {
                    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

                    const activeDetails =
                        params.row.activations_details?.filter((d: any) => d.status === true) || [];
                    const hasActive = activeDetails.length > 0;

                    const activeModes = params.row.activations_details
                        ?.filter((d: any) => d.status)
                        ?.map((d: any) => d.mode) ?? [];

                    const hasActiveMode = activeModes.includes("Active Mode");
                    const hasKoManual = activeModes.includes("KO Bidding Manual");
                    const hasKoAuto = activeModes.some((m: any) => m.startsWith("KO Bidding Auto"));

                    return (
                        <Box
                            sx={{
                                position: "relative",
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.8,
                                overflow: "hidden",
                                px: 1,
                            }}
                        >
                            {hasActive ? (
                                activeDetails.map((detail: any, i: number) => (
                                    <Tooltip
                                        key={i}
                                        arrow
                                        placement="top"
                                        disableInteractive={false}
                                        open={hoveredIndex === i}
                                        title={
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    gap: 1,
                                                    alignItems: "center",
                                                    pointerEvents: "auto"
                                                }}
                                                onMouseEnter={() => setHoveredIndex(i)}
                                                onMouseLeave={() => setHoveredIndex(null)}
                                            >
                                                {detail.mode !== "Active Mode" && (
                                                    <IconButton
                                                        size="small"
                                                        sx={{ "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
                                                        onClick={(e) => {
                                                            detail.mode === "KO Bidding Manual"
                                                                ? setModalActivationManual({
                                                                    open: true,
                                                                    isActive: true,
                                                                    row: params.row,
                                                                })
                                                                : setModalActivationAuto({
                                                                    open: true,
                                                                    isActive: true,
                                                                    row: params.row,
                                                                });
                                                        }}
                                                    >
                                                        <Iconify icon="solar:pen-bold" width={18} />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    size="small"
                                                    sx={{ "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setConfirmDisable({
                                                            open: true,
                                                            row: params.row,
                                                            mode: detail.mode,
                                                        });
                                                    }}
                                                >
                                                    <Iconify icon="solar:trash-bin-minimalistic-bold" width={18} />
                                                </IconButton>
                                            </Box>
                                        }
                                        componentsProps={{
                                            tooltip: {
                                                sx: {
                                                    pointerEvents: "auto",
                                                    p: 0.6,
                                                    borderRadius: 1,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    zIndex: 2500,
                                                },
                                            },
                                        }}
                                    >
                                        <Typography
                                            onMouseEnter={() => setHoveredIndex(i)}
                                            onMouseLeave={() => setHoveredIndex(null)}
                                            sx={{
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                py: 0.4,
                                                height: 28,
                                                display: "flex",
                                                alignItems: "center",

                                                color:
                                                    detail?.mode?.startsWith("KO Bidding Auto")
                                                        ? "#CEA8F0"
                                                        : detail.mode === "KO Bidding Manual"
                                                            ? "#8B92F9"
                                                            : "#DBC272",

                                                background:
                                                    detail?.mode?.startsWith("KO Bidding Auto")
                                                        ? "rgba(164, 111, 247, 0.19)"
                                                        : detail.mode === "KO Bidding Manual"
                                                            ? "rgba(111, 120, 247, 0.19)"
                                                            : "rgba(250, 176, 0, 0.08)",

                                                borderRadius: "6px",
                                                fontSize: 13,
                                                fontWeight: 500,
                                                textAlign: "center",
                                                transition: "all 0.2s ease",
                                                cursor: "pointer",
                                            }}
                                        >
                                            <Box sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        borderRadius: "4px",
                                                        p: .5,
                                                        background:
                                                            detail?.mode?.startsWith("KO Bidding Auto")
                                                                ? "rgba(164, 111, 247, 0.2)"
                                                                : detail.mode === "KO Bidding Manual"
                                                                    ? "rgba(111, 120, 247, 0.2)"
                                                                    : "rgba(250, 176, 0, 0.1)",
                                                    }}
                                                >
                                                    {detail.mode === "Active Mode"
                                                        ? (
                                                            <svg xmlns="http://www.w3.org/2000/svg"
                                                                width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                                <path d="M14.8982 16.7416C13.4745 17.776 11.7598 18.3331 10 18.3331C8.24018 18.3331 6.52553 17.776 5.1018 16.7416M13.6531 2.50985C15.2348 3.2813 16.5317 4.5337 17.3579 6.08753C18.1841 7.64137 18.4972 9.41687 18.2522 11.1596M1.74786 11.1595C1.50294 9.41679 1.81601 7.64129 2.64219 6.08745C3.46838 4.53362 4.76528 3.28122 6.347 2.50977M14.5833 9.9998C14.5833 12.5311 12.5313 14.5831 10 14.5831C7.4687 14.5831 5.41668 12.5311 5.41668 9.9998C5.41668 7.46849 7.4687 5.41646 10 5.41646C12.5313 5.41646 14.5833 7.46849 14.5833 9.9998Z"
                                                                    stroke="#DBC272" strokeWidth="1.66667"
                                                                    strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        )
                                                        : (
                                                            <Iconify icon="material-symbols:shield-outline-rounded" />
                                                        )
                                                    }
                                                </Box>

                                                <Typography sx={{ px: 1 }} fontSize={13}>
                                                    {detail?.mode?.startsWith("KO Bidding Auto")
                                                        ? "Auto"
                                                        : detail.mode === "KO Bidding Manual"
                                                            ? "Manual"
                                                            : "Active"
                                                    }
                                                </Typography>
                                            </Box>
                                        </Typography>

                                    </Tooltip>
                                ))
                            ) : (
                                <Typography
                                    sx={{
                                        flex: 1,
                                        color: "rgba(255,255,255,0.4)",
                                        borderRadius: "6px",
                                        px: 1.2,
                                        py: 0.4,
                                        fontSize: 13,
                                        fontWeight: 500,
                                        textAlign: "center",
                                    }}
                                >
                                    --
                                </Typography>
                            )}

                            {!(hasActiveMode && (hasKoManual || hasKoAuto)) && (

                                <Box
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setModalCreateStrategy({
                                            open: true,
                                            row: [params.row],
                                        });
                                    }}
                                    sx={{
                                        position: "absolute",
                                        right: 6,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        width: 22,
                                        height: 22,
                                        borderRadius: "6px",
                                        backgroundColor: "rgba(255,255,255,0.05)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        opacity: 0,
                                        transition: "all 0.2s ease",
                                        "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
                                        ".MuiDataGrid-cell:hover &": { opacity: 1 },
                                    }}
                                >
                                    <Typography sx={{ fontSize: 20, lineHeight: 1, color: "#fff" }}>+</Typography>
                                </Box>
                            )}
                        </Box>
                    );
                }
            },
            {
                field: 'fitted_trend_top_blended_presence_rate',
                renderHeader: (params) => (
                    <CustomTooltip title={"How often your domain appears in the top section, whether paid or organic."} placement="top">
                        <Box>
                            <span style={{ fontWeight: 'bold' }}>Smart Top</span>
                            <ColumnBadge bgColor={"#2D4555"} color={"#B6DDFF"} title={"Most saving"} field="fitted_trend_top_blended_presence_rate" />
                        </Box>
                    </CustomTooltip>
                ),
                flex: 1,
                cellClassName: 'sparkline-cell',
                minWidth: 170,
                renderCell: (params: any) => {
                    const points = params.row.fitted_trend_top_blended_presence_rate || [];
                    if (!points.length) return <span style={{ opacity: 0.5 }}>--</span>;

                    const koAuto = params.row.activations_details?.find(
                        (d: any) => d?.status === true && d?.mode?.startsWith("KO Bidding Auto")
                    );

                    const koManual = params.row.activations_details?.find(
                        (d: any) => d?.status === true && d?.mode?.startsWith("KO Bidding Manual")
                    );

                    const series = [{
                        name: 'Smart Top',
                        data: points.map((point: any, i: number) => ({
                            x: Number(point.max_cpc),
                            y: Number(point.fitted_trend_top_blended_presence_rate),
                        })),

                    }];

                    return (
                        <Box onClick={(e) => {
                            e.stopPropagation();

                            const auto = params.row.activations_details?.some(
                                (d: any) => d.status && d.mode?.startsWith("KO Bidding Auto")
                            );

                            if (auto) {
                                setModalActivationAuto({
                                    open: true,
                                    isActive: true,
                                    row: params.row,
                                });
                            } else {
                                setModalActivationManual({
                                    open: true,
                                    isActive: true,
                                    row: params.row,
                                });
                            }
                        }}
                            px={2}
                            sx={{
                                cursor: "pointer",
                                pointerEvents: "auto",
                            }}
                            height={'100%'}
                            bgcolor={koAuto?.mode?.split("-")[1]?.trim() === "Smart Top" ? 'rgba(0, 70, 102, 0.1)' : 'transparent'}
                        >
                            <CustomLineChart maxCpcValue={koManual?.max_cpc} series={series} color="#00B8D9" height={45} />
                        </Box>
                    );
                },
            },
            {
                field: 'fitted_trend_top_paid_presence_rate',
                headerName: 'Smart First',
                flex: 1,
                cellClassName: 'sparkline-cell',
                renderHeader: () => (
                    <CustomTooltip title={"How often your domain appears in the first position in the top section, whether paid or organic."} placement="top">
                        <span style={{ fontWeight: 'bold' }}>Smart First</span>
                    </CustomTooltip>
                ),
                minWidth: 170,
                renderCell: (params: any) => {
                    const points = params.row.fitted_trend_top_paid_presence_rate || [];
                    if (!points.length) return <span style={{ opacity: 0.5 }}>--</span>;

                    const koAuto = params.row.activations_details?.find(
                        (d: any) => d?.status === true && d?.mode?.startsWith("KO Bidding Auto")
                    );


                    const koManual = params.row.activations_details?.find(
                        (d: any) => d?.status === true && d?.mode?.startsWith("KO Bidding Manual")
                    );

                    const series = [{
                        name: 'Smart First',
                        data: points.map((point: any) => ({
                            x: Number(point?.max_cpc),
                            y: point?.fitted_trend_top_paid_presence_rate,
                        })),
                    }];

                    return (
                        <Box px={2} height={'100%'} bgcolor={koAuto?.mode?.split("-")[1]?.trim() === "Smart First" ? 'rgba(194, 129, 251, 0.08)' : 'transparent'}>
                            <CustomLineChart maxCpcValue={koManual?.max_cpc} series={series} color="rgba(185, 133, 244, 1)" height={45} />
                        </Box>
                    );
                },
            },
            {
                field: 'fitted_trend_abs_top_blended_presence_rate',
                headerName: "Paid Top",
                flex: 1,
                cellClassName: 'sparkline-cell',
                renderHeader: () => (
                    <CustomTooltip title={"How often your domain appears in the paid top ads section."} placement="top">
                        <span style={{ fontWeight: 'bold' }}>Paid Top</span>
                    </CustomTooltip>
                ),
                minWidth: 180,
                renderCell: (params: any) => {
                    const points = params.row.fitted_trend_abs_top_blended_presence_rate || [];
                    if (!points.length) return <span style={{ opacity: 0.5 }}>--</span>;

                    const koAuto = params.row.activations_details?.find(
                        (d: any) => d?.status === true && d?.mode?.startsWith("KO Bidding Auto")
                    );


                    const koManual = params.row.activations_details?.find(
                        (d: any) => d?.status === true && d?.mode?.startsWith("KO Bidding Manual")
                    );

                    const series = [{
                        name: 'Paid Top',
                        data: points.map((point: any) => ({
                            x: Number(point?.max_cpc),
                            y: point?.fitted_trend_abs_top_blended_presence_rate,
                        })),
                    }];

                    return (
                        <Box height={'100%'}
                            px={2} bgcolor={koAuto?.mode?.split("-")[1]?.trim() === "Paid Top" ? 'rgba(248, 204, 203, 0.10)' : 'transparent'}>
                            <CustomLineChart maxCpcValue={koManual?.max_cpc} series={series} color="#F8CCCB" height={45} />
                        </Box>
                    );
                },
            },
            {
                field: 'fitted_trend_abs_top_paid_presence_rate',
                headerName: "Paid First",
                flex: 1,
                cellClassName: 'sparkline-cell',
                renderHeader: () => (
                    <CustomTooltip title={"How often your domain appears in the first position in the paid top ads section. "} placement="top">
                        <Box>
                            <span style={{ fontWeight: 'bold' }}>Paid First</span>
                            <ColumnBadge bgColor={"#332C56"} color={"#EBD6FD"} title={"Most visibility"} field="fitted_trend_abs_top_paid_presence_rate" />
                        </Box>
                    </CustomTooltip>
                ),
                minWidth: 180,
                renderCell: (params: any) => {
                    const points = params.row.fitted_trend_abs_top_paid_presence_rate || [];
                    if (!points.length) return <span style={{ opacity: 0.5 }}>--</span>;


                    const koAuto = params.row.activations_details?.find(
                        (d: any) => d?.status === true && d?.mode?.startsWith("KO Bidding Auto")
                    );

                    const koManual = params.row.activations_details?.find(
                        (d: any) => d?.status === true && d?.mode?.startsWith("KO Bidding Manual")
                    );

                    const series = [{
                        name: 'Paid First',
                        data: points.map((point: any) => ({
                            x: Number(point?.max_cpc),
                            y: point?.fitted_trend_abs_top_paid_presence_rate,
                        })),
                    }];

                    return (
                        <Box
                            height={'100%'}
                            px={2}
                            bgcolor={koAuto?.mode?.split("-")[1]?.trim() === "Paid First" ? 'rgba(255, 225, 128, 0.10)' : 'trasnparent'}>
                            <CustomLineChart maxCpcValue={koManual?.max_cpc} series={series} color="rgba(255, 225, 128, 1)" height={45} />
                        </Box>
                    );
                },
            },
            {
                field: "max_cpc",
                headerName: "Max CPC",
                renderHeader: () => (
                    <div
                        style={{

                            width: "100%",
                            whiteSpace: "nowrap",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "start",
                            textAlign: "right",
                        }}
                    >
                        <span style={{ fontWeight: 'bold' }}>Max CPC</span>
                    </div>
                ),
                minWidth: 80,
                editable: true,
                renderCell: (params: GridRenderCellParams) => {
                    const koBiddingDetail = params.row.activations_details?.find(
                        (d: any) => d.mode === "KO Bidding Manual"
                    );

                    const value =
                        koBiddingDetail?.status === true
                            ? koBiddingDetail.max_cpc
                            : null;

                    return (
                        <Box display="flex" height="100%" py={1} px={.5}>
                            <Box
                                sx={{
                                    borderRadius: '6px',
                                    cursor: value ? 'pointer' : 'not-allowed',
                                    border: '1px solid rgba(145, 158, 171, 0.16)',
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '6px 8px',

                                    ...(value && {
                                        '&:hover': {
                                            borderColor: '#338AF3',
                                            backgroundColor: 'rgba(51, 138, 243, 0.15)',
                                            boxShadow: '0 0 0 2px rgba(51, 138, 243, 0.20)',
                                        }
                                    }),
                                }}
                            >
                                {value !== null && value !== undefined
                                    ? formatCurrency(value, workspace?.currency, lang, true, 2)
                                    : <span style={{ opacity: 0.5 }}>--</span>}
                            </Box>
                        </Box>
                    );
                },
            },
        ],
        [handleToggle]
    );

    const rowsToDisplay = useMemo(() => {
        const rowsArray = Array.isArray(modalScraping.row)
            ? modalScraping.row
            : modalScraping.row
                ? [modalScraping.row]
                : [];

        let result: any[] = [];

        for (const r of rowsArray) {
            const hasActive = r.scraping_details?.some((s: any) => s.status);

            if (hasActive) {
                result.push(
                    ...r.scraping_details.map((s: any, i: number) => ({
                        ...s,
                        id: s.id ?? `${r.id}-react-${i}`,
                        status: false,
                    }))
                );
            } else {
                result.push(
                    ...r.country_name.map((country: string, i: number) => ({
                        id: `${r.id}-new-${i}`,
                        keyword: r.keyword,
                        platform_scraping: ["Google"],
                        country,
                        frequency: "60 min",
                        device: ["desktop", "mobile"],
                        status: true,
                        country_code: r.country_code,
                    }))
                );
            }
        }

        return result;
    }, [modalScraping]);

    const handleDisableActiveMode = useCallback(
        async (detail: any) => {
            if (!workspace) return;

            try {
                await disableActiveMode({
                    id: detail.id,
                    workspace_id: workspace.id,
                }).unwrap();

                enqueueSnackbar(`Active Mode disabled successfully`, { variant: "success" });

                const rowToPatch = confirmDisable.row;

                if (!rowToPatch) return;

                setLocalPatches(prev => ({
                    ...prev,
                    [rowToPatch.id]: {
                        ...rowToPatch,
                        activations_details: rowToPatch.activations_details.filter(
                            (d: any) => d.mode !== "Active Mode"
                        )
                    }
                }));

            } catch (err: any) {
                console.error(err);
                enqueueSnackbar(`Failed to disable Active Mode`, { variant: "error" });
            }
        },
        [disableActiveMode, workspace, confirmDisable.row, setLocalPatches]
    );

    const handleConfirmDisable = useCallback(
        async (row: any) => {
            if (!row) return;

            const modeToDisable = confirmDisable.mode;

            try {
                if (modeToDisable === "Active Mode") {
                    const detail = row.activations_details?.find((d: any) => d.mode === "Active Mode");
                    await handleDisableActiveMode(detail);
                }

                else if (modeToDisable === "Scraping") {
                    const detail = row.scraping_details?.[0];
                    await handleDisableScraping(detail);
                }

                else if (modeToDisable === "KO Bidding Manual") {
                    const detail = row.activations_details?.find((d: any) => d.mode === "KO Bidding Manual");

                    if (detail?.id) {
                        await disableKoBiddingManualMaxCpc({ id: detail.id }).unwrap();

                        enqueueSnackbar(`KO Bidding Manual disabled successfully`, { variant: "success" });

                        setLocalPatches(prev => {
                            const existing = prev[row.id] ?? row;

                            return {
                                ...prev,
                                [row.id]: {
                                    ...existing,
                                    activations_details: existing.activations_details.filter(
                                        (d: any) => d.mode !== "KO Bidding Manual"
                                    )
                                }
                            };
                        });

                    }
                }

                else if (modeToDisable?.startsWith("KO Bidding Auto")) {
                    const detail = row.activations_details?.find(
                        (d: any) => d.mode?.startsWith("KO Bidding Auto")
                    );

                    if (detail?.id) {
                        await disableKoBiddingAutoMaxCpc({ id: detail.id }).unwrap();

                        enqueueSnackbar(`KO Bidding Auto disabled successfully`, { variant: "success" });

                        // 🔥 Patch local instantané
                        setLocalPatches(prev => ({
                            ...prev,
                            [row.id]: {
                                ...row,
                                activations_details: row.activations_details.filter(
                                    (d: any) => !d.mode?.startsWith("KO Bidding Auto")
                                )
                            }
                        }));
                    }
                }

                setConfirmDisable({ open: false, row: null, mode: null });

            } catch (err) {
                console.error("❌ Error disabling mode:", err);
                enqueueSnackbar(`Failed to disable ${modeToDisable}`, { variant: "error" });
            }
        },
        [
            confirmDisable,
            handleDisableActiveMode,
            handleDisableScraping,
            disableKoBiddingManualMaxCpc,
            disableKoBiddingAutoMaxCpc,
            setLocalPatches
        ]
    );


    const CustomToolbar = ({ selectionRows = [] }: any) => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <Box sx={{ display: "flex", gap: 1 }}>
                    <GridToolbarColumnsButton />
                    <GridToolbarFilterButton />
                    <GridToolbarExport csvOptions={{ allColumns: true }} />
                </Box>

                {selection.length > 1 && (
                    <Box sx={{ pr: 2 }}>
                        <Button
                            onClick={() =>
                                setModalScraping({
                                    open: true,
                                    row: selectionRows,
                                })
                            }
                            sx={{ mr: 1 }}
                            size="small"
                            variant="outlined"
                        >
                            Active Scraping ({selectionRows.length})
                        </Button>

                        <Button onClick={() =>
                            setModalCreateStrategy({
                                open: true,
                                row: selectionRows,
                            })
                        }
                            size="small"
                            variant="contained"
                        >
                            Activate Bidding Strategy ({selectionRows.length})
                        </Button>
                    </Box>
                )}
            </GridToolbarContainer>
        );
    };

    const handleConfirmScraping = useCallback(async () => {
        const rows = modalScraping.row;
        if (!rows || !workspace) return;

        setLoading(true);

        try {
            const details = scrapingDetails.length > 0 ? scrapingDetails : rowsToDisplay;

            const payloadArray: any = details.map((detail, index) => {
                const originalRow = rows[index] ?? rows[0];

                return {
                    keyword: detail.keyword ?? originalRow.keyword,
                    country: Array.isArray(detail.country) ? detail.country : [detail.country],
                    country_code: originalRow.country_code,
                    home_language: originalRow.home_language,
                    frequency: detail.frequency ?? "60 min",
                    platform_scraping: detail.platform_scraping ?? ["Google"],
                    device: detail.device ?? ["desktop", "mobile"],
                    workspaceId: workspace.id,
                    client: workspace.bqTable,
                    mode: "Scraping",
                };
            });

            const result = await addScraping(payloadArray).unwrap();

            enqueueSnackbar(`Scraping added successfully.`, { variant: "success" });

            rows.forEach((r: any, rowIndex: any) => {
                setLocalPatches(prev => ({
                    ...prev,
                    [r.id]: {
                        ...prev[r.id],
                        scraping_details: result.map((newScraping: any) => ({
                            ...newScraping,
                            status: true
                        }))
                    }
                }));
            });

            handleCloseScraping();

        } catch (err) {
            console.error(err);
            enqueueSnackbar(`Failed to add scraping`, { variant: "error" });
        } finally {
            setLoading(false);
        }
    }, [
        modalScraping.row,
        workspace,
        scrapingDetails,
        rowsToDisplay,
        addScraping,
        setLocalPatches,
        handleCloseScraping
    ]);



    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
        campaign_ids: false,
        ad_group_names: false,
        ad_group_ids: false,
        keyword_id: false,
        region_name: false,
    });

    const handleChangeColumnVisibilityModel = useCallback((newModel: GridColumnVisibilityModel) => {
        setColumnVisibilityModel(newModel);
    }, []);


    return (
        <>
            <Box
                sx={{
                    "& .MuiDataGrid-columnHeader.keyword-header .MuiDataGrid-columnHeaderTitleContainer": {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                    },

                    "& .MuiDataGrid-columnHeader.keyword-header .MuiDataGrid-columnHeaderTitleContainerContent": {
                        display: "flex",
                        flex: 1,
                    },

                    "& .keyword-header-wrapper": {
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                    },
                    position: "relative",
                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
                        outline: "none !important",
                    },
                    "& .MuiDataGrid-cell--pinnedLeft": {
                        backgroundColor: theme.palette.mode === "dark" ? "#212b36" : "white",
                    },
                    "& .MuiDataGrid-cell": {
                        borderBottom: "1px solid rgba(255,255,255,0.06)",
                    },
                    "& .MuiDataGrid-row": {
                        borderBottom: "1px solid rgba(255,255,255,0.06)",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        borderBottom: "1px solid rgba(255,255,255,0.10)",
                    },
                    "& .MuiDataGrid-columnSeparator": {
                        color: "rgba(255,255,255,0.12)",
                        visibility: "visible",
                    },
                    "& .MuiDataGrid-columnHeader": {
                        borderRight: "1px solid rgba(255,255,255,0.10)",
                    },
                    "& .MuiDataGrid-cell:not(:last-of-type)": {
                        borderRight: "1px solid rgba(255,255,255,0.06)",
                    },
                }}
            >
                <DataGridPro
                    autoHeight
                    getRowId={(r) => r.id}
                    rows={mergedRows}
                    onRowSelectionModelChange={(newSelection: any) => {
                        setSelection(newSelection);

                        const selectedRows: any = data.filter(row => newSelection.includes(row.id));
                        setSelectionRows(selectedRows);
                    }}
                    columns={columns}
                    rowCount={rowCount}
                    checkboxSelection
                    onCellClick={(params, event) => {
                        if (params.field !== "max_cpc") return;

                        const row = params?.row;
                        if (!row || !row.activations_details?.length) return;

                        event.stopPropagation();

                        const hasManualWithCpc = row.activations_details.some(
                            (d: any) =>
                                d.mode === "KO Bidding Manual" &&
                                d.status === true &&
                                d.max_cpc !== null &&
                                d.max_cpc !== undefined
                        );

                        if (hasManualWithCpc) {
                            setModalActivationManual({
                                open: true,
                                isActive: true,
                                row,
                            });
                        }
                    }}
                    pagination
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={handleChangeColumnVisibilityModel}
                    rowBufferPx={0}
                    disableRowSelectionOnClick
                    paginationMode="server"
                    paginationModel={{ page, pageSize }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    onPaginationModelChange={({ page, pageSize }) => {
                        onPageChange(page);
                        onPageSizeChange(pageSize);
                    }}
                    onCellEditStart={(params, event) => {
                        if (params.field === 'max_cpc') {
                            event.defaultMuiPrevented = true;

                            const hasKOBiddingManual =
                                params.row.activations_details?.some((d: any) => d.mode === 'KO Bidding Manual' && d.status === true) ?? false;

                            if (hasKOBiddingManual) {
                                setModalActivationManual({
                                    open: true,
                                    isActive: true,
                                    row: params.row,
                                });
                            }
                        }
                    }}
                    slots={{
                        toolbar: () => <CustomToolbar selectionRows={selectionRows} />,
                    }}

                    sx={{
                        "& .MuiDataGrid-footerContainer": {
                            backgroundColor: theme.palette.mode === "dark" ? "#1C252E" : "#F9FAFB",
                        },
                    }}
                />
            </Box>

            <ModalScraping
                open={modalScraping.open}
                loading={loading}
                rowsToDisplay={rowsToDisplay}
                onClose={handleCloseScraping}
                onConfirm={handleConfirmScraping}
                setModalScraping={setModalScraping}
                setScrapingDetails={setScrapingDetails}
            />

            <ModalActivationsManual
                open={modalActivationManual.open && modalActivationManual.isActive}
                row={modalActivationManual.row}
                onClose={handleCloseActivationManual}
                setModalActivation={setModalActivationManual}
                setLocalPatches={setLocalPatches}
            />

            <ModalActivationsAuto
                open={modalActivationAuto.open && modalActivationAuto.isActive}
                row={modalActivationAuto.row}
                onClose={handleCloseActivationAuto}
                setLocalPatches={setLocalPatches}
            />

            <ModalCreateStrategy
                open={modalCreateStrategy.open}
                row={modalCreateStrategy.row}
                onClose={() => setModalCreateStrategy({ open: false, isActive: false, row: null })}
                setLocalPatches={setLocalPatches}
            />

            <ConfirmDisableModal
                open={confirmDisable.open}
                row={confirmDisable.row}
                onClose={() => setConfirmDisable({ open: false, row: null, mode: null })}
                onConfirm={handleConfirmDisable}
            />
        </>
    );
}